/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {useEffect, useState} from "react";
import { BrowserRouter, Routes, Route, Navigate} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Messenger from "./components/Messenger";
import { AppDispatch, RootState } from "./redux/store";
import monday from 'monday-sdk-js';
import { useMondayTheme } from "./hooks/useMondayTheme";

import {
  initializeMondaySession,
  viewerBlockedMessage,
  WHATSABLE_AUTH_REQUIRED_MESSAGE,
  WHATSAPP_ONBOARDING_URL,
  WHATSAPP_CONNECT_MESSAGE,
} from "./redux/reducers/authSlice";
import {
  buildNotifyerMondayAuthUrl,
  NOTIFYER_MONDAY_AUTH_BASE_URL,
  parseMondayContextData,
} from "./Utils/mondayAuthUrl";
// import { fetchDealDetails, fetchPersonDetails } from "./redux/reducers/dealPersonDetailsSlice";
import { fetchItemDetails } from "./redux/reducers/itemDetailsSlice";
import GlobalNotification from "./components/GlobalNotification";
import { NotifyerHelpLinksBar } from "./components/NotifyerHelpLinksBar";

/** Full-screen states share the thin help strip (docs + video). */
function FullScreenWithHelpBar({
  className,
  children,
}: {
  className: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`flex h-screen w-full flex-col ${className}`}>
      <NotifyerHelpLinksBar />
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-auto p-4">
        {children}
      </div>
    </div>
  );
}

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const {
    isAuthenticated,
    isAuthenticating,
    error,
    authorizationRequired,
    whatsappSetupRequired,
    viewerBlocked,
  } = useSelector((state: RootState) => state.auth);
  const [authUrl, setAuthUrl] = useState(NOTIFYER_MONDAY_AUTH_BASE_URL);

  useEffect(() => {
    if (!authorizationRequired) return;

    let cancelled = false;
    monday()
      .get("context")
      .then((res: { data?: unknown }) => {
        if (cancelled) return;
        setAuthUrl(
          buildNotifyerMondayAuthUrl(parseMondayContextData(res?.data))
        );
      })
      .catch(() => {
        if (cancelled) return;
        setAuthUrl(buildNotifyerMondayAuthUrl({}));
      });

    return () => {
      cancelled = true;
    };
  }, [authorizationRequired]);

  // Show loading while authenticating
  if (isAuthenticating) {
    return (
      <FullScreenWithHelpBar className="bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="flex flex-col items-center gap-8">
          {/* Logos */}
          <div className="flex items-center gap-4">
            {/* Monday Logo */}
            <div className="flex items-center justify-center w-20 h-20 bg-white rounded-xl shadow-lg p-3">
              <img 
                src="/monday-200x200-1.png"
                alt="Monday.com Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* Plus Icon */}
            <div className="text-gray-400 text-xl font-light">+</div>
            
            {/* Notifyer Logo */}
            <div className="flex items-center justify-center w-20 h-20 bg-white rounded-xl shadow-lg p-3">
              <img 
                src="/notifyer.svg"
                alt="Notifyer Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* Plus Icon */}
            <div className="text-gray-400 text-xl font-light">+</div>
            
            {/* WhatsAble App Logo */}
            <div className="flex items-center justify-center w-20 h-20 bg-white rounded-xl shadow-lg p-3">
              <img 
                src="/whatsable-logo-transparent.svg"
                alt="WhatsAble Logo" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Loading Spinner */}
          <div className="relative">
            <div className="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
          </div>
          
          <div className="flex flex-col items-center gap-2">
            <span className="text-lg font-semibold text-gray-900">
              Verifying your session...
            </span>
            <span className="text-sm text-gray-500">
              Checking WhatsApp connection
            </span>
          </div>
        </div>
      </FullScreenWithHelpBar>
    );
  }

  if (!isAuthenticated && viewerBlocked) {
    return (
      <FullScreenWithHelpBar className="bg-default-background">
        <div className="flex max-w-lg flex-col items-center gap-6 text-center">
          <div className="w-16 h-16 rounded-full bg-neutral-200 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-neutral-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-default-font">
              Viewer access
            </h2>
            <p className="text-subtext-color">{viewerBlockedMessage()}</p>
            <p className="text-sm text-neutral-600">
              Ask a workspace member or admin if you need messaging access from
              this item.
            </p>
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 border border-neutral-border text-default-font rounded-md hover:bg-neutral-100 transition-colors"
          >
            Retry session
          </button>
        </div>
      </FullScreenWithHelpBar>
    );
  }

  if (!isAuthenticated && whatsappSetupRequired) {
    return (
      <FullScreenWithHelpBar className="bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="flex max-w-lg flex-col items-center gap-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-green-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">
              WhatsApp setup
            </h2>
            <p className="text-gray-600">{WHATSAPP_CONNECT_MESSAGE}</p>
            <p className="text-sm text-gray-500">
              A new tab may have opened to connect WhatsApp. If not, use the
              button below.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href={WHATSAPP_ONBOARDING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              Open WhatsApp connection
            </a>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 border border-gray-300 text-gray-800 rounded-md hover:bg-gray-50 transition-colors"
            >
              Retry session
            </button>
          </div>
        </div>
      </FullScreenWithHelpBar>
    );
  }

  if (!isAuthenticated && authorizationRequired) {
    return (
      <FullScreenWithHelpBar className="bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="flex max-w-lg flex-col items-center gap-6 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-amber-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">
              Connect WhatsAble Notifyer
            </h2>
            <p className="text-gray-600">{WHATSABLE_AUTH_REQUIRED_MESSAGE}</p>
            <p className="text-sm text-gray-500">
              Use the button below to complete authorization.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() =>
                window.open(authUrl, "_blank", "noopener,noreferrer")
              }
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              Authorization
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 border border-gray-300 text-gray-800 rounded-md hover:bg-gray-50 transition-colors"
            >
              Retry session
            </button>
          </div>
        </div>
      </FullScreenWithHelpBar>
    );
  }

  // Show error message if authentication failed
  if (!isAuthenticated && error) {

    return (
      <FullScreenWithHelpBar className="bg-default-background">
        <div className="flex flex-col items-center gap-6 max-w-md mx-auto text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Failed</h2>
            <p className="text-gray-600 mb-4">
              We couldn't verify your credentials. Please check your userId and companyId parameters and try again.
            </p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </FullScreenWithHelpBar>
    );
  }

  // Show initial state message if not authenticated yet
  if (!isAuthenticated) {
    return (
      <FullScreenWithHelpBar className="bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="flex flex-col items-center gap-8 max-w-md mx-auto text-center">
          {/* Logos */}
          <div className="flex items-center gap-4">
            {/* Monday Logo */}
            <div className="flex items-center justify-center w-24 h-24 bg-white rounded-2xl shadow-xl p-4 transform transition-transform hover:scale-105">
              <img 
                src="/monday-200x200-1.png"
                alt="Monday.com Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* Plus Icon */}
            <div className="text-gray-400 text-2xl font-light">+</div>
            
            {/* Notifyer Logo */}
            <div className="flex items-center justify-center w-24 h-24 bg-white rounded-2xl shadow-xl p-4 transform transition-transform hover:scale-105">
              <img 
                src="/notifyer.svg" 
                alt="Notifyer Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* Plus Icon */}
            <div className="text-gray-400 text-2xl font-light">+</div>
            
            {/* WhatsAble App Logo */}
            <div className="flex items-center justify-center w-24 h-24 bg-white rounded-2xl shadow-xl p-4 transform transition-transform hover:scale-105">
              <img 
                src="/whatsable-logo-transparent.svg"
                alt="WhatsAble Logo" 
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Welcome Content */}
          <div className="flex flex-col items-center gap-4">
            <h2 className="text-3xl font-bold text-gray-900">Welcome</h2>
            <p className="text-lg text-gray-600 max-w-sm">
              Please wait while we verify your credentials and set up your workspace...
            </p>
            
            {/* Animated Dots */}
            <div className="flex gap-2 mt-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      </FullScreenWithHelpBar>
    );
  }

  
  return children;
};

function AppContent() {
  const dispatch = useDispatch<AppDispatch>();
  useMondayTheme();

  useEffect(() => {
    const mondayClient = monday();
    mondayClient.get("sessionToken").then((res: any) => {
      const sessionToken = res.data;
      dispatch(initializeMondaySession({ sessionToken }));
    });

    mondayClient.listen("context", (res: any) => {
      const {account, user, itemId} = res.data;
      const mondayAccountId = account.id;
      if (account.id && user.id) {

        dispatch(fetchItemDetails({ 
          accountId: mondayAccountId, 
          itemId: itemId.toString() 
        }));
      }
    });
    
  }, [dispatch]);


  return (
    <>
      <GlobalNotification />
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Messenger />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
